import type { InjectionKey } from "vue";

/** Where one prompted message is in its rating flow. */
export type ResponseRatingStatus = "idle" | "sending" | "sent";

export interface ResponseRatingState {
  /** The usage row's requestId — what the rating is written against. */
  interactionId: string;
  status: ResponseRatingStatus;
  /** The committed score, once one has been sent. */
  rating: number | null;
}

/**
 * Lets the rating prompt rendered deep inside AssistantMessage talk to the conversation-level rating
 * state owned by useAgentChat — the same provide/inject shape as ApprovalResponderKey, for the same
 * reason: the generic message router components should not grow rating-specific emits.
 */
export interface ResponseRatingController {
  /** The prompt state for a message, or null when this message was not picked for a rating prompt. */
  promptFor: (messageId: string) => ResponseRatingState | null;
  /** Sends (or re-sends, with a comment) the rating for a prompted message. Never throws — failures toast. */
  submit: (messageId: string, rating: number, comment?: string) => Promise<void>;
  /** Removes the prompt (unrated, or once it has served its purpose). */
  dismiss: (messageId: string) => void;
  /** Removes every open prompt — the user moved on by sending a new message. */
  dismissAll: () => void;
}

export const ResponseRatingKey: InjectionKey<ResponseRatingController> = Symbol("chat-response-rating");
